import { Component, OnInit } from '@angular/core';
import { RepositoryService } from '../../services/repository/repository.service';
import { OrderListAction } from '../../enums/OrderListAction';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, ModalController } from '@ionic/angular';
import Article from '../../../models/Article';
import ArticleOption from '../../../models/ArticleOption';
import * as moment from 'moment';
import { MatSnackBar } from '@angular/material/snack-bar';
import Utils from '../../../utils';
import { MenuPage } from '../menu/menu.page';
import { CheckoutModalComponent } from '../../components/checkout-modal/checkout-modal.component';
import { PreorderType } from '../../../enums/PreorderType';
import { ModalInfoComponent } from '../../components/modal-info/modal-info.component';
import { AppComponent } from '../../app.component';
import RepositoryInterface from '../repository-interface';
import { TranslateService } from '@ngx-translate/core';
import { OrderUtils } from '../../../utils/order-utils';
import { environment } from '../../../environments/environment';
import { AngularFireAnalytics } from '@angular/fire/analytics';

@Component({
	selector: 'app-order',
	templateUrl: './order.page.html',
	styleUrls: ['order.page.scss'],
})
export class OrderPage extends RepositoryInterface implements OnInit {
	static url = 'order';
	static failUrlWithPaymentParam = 'payment/fail/:payment';
	static cancelUrlWithPaymentParam = 'payment/cancel/:payment';
	static paymentFailUrl = 'payment/fail';
	static paymentCancelUrl = 'payment/cancel';

	moment = moment;

	isValid = false;
	loading = false;

	utils = Utils;
	pt = PreorderType;
	differenceToMvo: number;

	constructor(
		private translate: TranslateService,
		protected repository: RepositoryService,
		private modalCtrl: ModalController,
		private snackbarCtrl: MatSnackBar,
		private router: Router,
		private route: ActivatedRoute,
		private alertCtrl: AlertController,
		private analytics: AngularFireAnalytics
	) {
		super(repository);
		this.validate();
	}

	static navigate(router: Router) {
		return router.navigateByUrl(AppComponent.largeScreen ? MenuPage.url : OrderPage.url);
	}

	onVenue() {
		super.onVenue();
		this.validate();
	}

	onOrder() {
		super.onOrder();
		this.validate();
	}

	ngOnInit(): void {
		super.ngOnInit();
		switch (this.route.snapshot.routeConfig.path) {
			case OrderPage.url:
				break;
			case OrderPage.paymentCancelUrl:
				this.snackbarCtrl.open(this.translate.instant('order.payment_cancel'), null, {
					duration: 2000,
				});
				break;
			case OrderPage.paymentFailUrl:
				this.snackbarCtrl.open(this.translate.instant('order.payment_fail'), null, {
					duration: 5000,
				});
				break;
		}
	}

	async onAction(index: number, event: OrderListAction) {
		switch (event) {
			case OrderListAction.add:
				this.order.orderedArticles[index].quantity += 1;
				break;
			case OrderListAction.delete:
				this.order.orderedArticles.splice(index, 1);
				break;
			case OrderListAction.note:
				const alert = await this.alertCtrl.create({
					mode: 'md',
					header: this.translate.instant('order.note.header'),
					inputs: [
						{
							type: 'textarea',
							placeholder: this.translate.instant('order.note.placeholder'),
							value: this.order.orderedArticles[index].note,
							name: 'note',
						},
					],
					buttons: [
						{
							text: this.translate.instant('order.note.cancel'),
							handler: () => {
								alert.dismiss();
							},
						},
						{
							text: this.translate.instant('order.note.ok'),
							handler: value => {
								console.log(value);
								this.order.orderedArticles[index].note = value.note;
								alert.dismiss();
							},
						},
					],
				});
				await alert.present();
				await alert.onDidDismiss();
				break;
			case OrderListAction.edit:
				const modal = await this.modalCtrl.create({
					cssClass: AppComponent.largeScreen ? 'large-modal' : '',
					component: ModalInfoComponent,
					componentProps: {
						// copy article group
						articleGroup: JSON.parse(JSON.stringify(this.order.orderedArticles[index])),
						recommend: false
					},
					showBackdrop: true,
					backdropDismiss: true,
				});
				await modal.present();
				const response = await modal.onDidDismiss();
				if (response.data && response.data.articleGroup) {
					this.order.orderedArticles[index] = response.data.articleGroup;
					this.repository.order.emit(this.order);
				}
				break;
			case OrderListAction.remove:
				if (this.order.orderedArticles[index].quantity > 1) {
					this.order.orderedArticles[index].quantity -= 1;
				} else {
					await this.onAction(index, OrderListAction.delete);
					return;
				}
				break;
		}
		this.repository.order.emit(this.order);
	}

	backToMenu() {
		MenuPage.navigate(this.router);
	}

	async openCheckoutModal() {
		if (!this.isValid) {
			return;
		}
		this.loading = true;
		const modal = await this.modalCtrl.create({
			component: CheckoutModalComponent,
			showBackdrop: true,
			cssClass: 'checkout-modal-full',
			backdropDismiss: true,
		});
		await modal.present();
		const result = await modal.onDidDismiss();
		if (result.data === undefined) {
			this.loading = false;
			return;
		}
		this.order.orderAt = result.data.orderAt;
		this.order.preorder = result.data.preorder;
		this.repository.order.emit(this.order);
		try {
			await Utils.sendOrder(
				this.router,
				this.modalCtrl,
				this.translate,
				this.repository,
				this.order,
				loading => (this.loading = loading),
				this.snackbarCtrl,
				this.analytics
			);
		} catch (e) {
			console.log(JSON.parse(JSON.stringify(e)));
		}
	}

	validate() {
		if (!this.order || !this.venue || !this.order.preorder) {
			this.isValid = false;
			return;
		}
		const mov = this.order.preorder.type === PreorderType.DELIVERY ? +this.venue.movDelivery.$numberDecimal : 0;
		const orderValue = OrderUtils.articleGroupsTotalPrice(
			this.order.orderedArticles,
			this.order.type,
			this.order.preorder.type,
			this.order.terminalorder ? this.order.terminalorder.type : null
		);
		this.differenceToMvo = orderValue - mov;
		this.isValid = this.differenceToMvo >= 0;
	}

	totalPrice(): string {
		return Utils.numberToCurrency(OrderUtils.orderTotalPrice(this.order), this.order.currency);
	}

	replace(index: number, article: Article, options: ArticleOption[]) {
		this.order.orderedArticles[index].quantity = 1;
		this.order.orderedArticles[index].article = article;
		this.order.orderedArticles[index].groups = options;
	}
}
